import JsBarcode from 'jsbarcode'
import { notice } from '@/components/pbo/notice'
import { GetInboundDetailsItem } from '@/services/inbound/inboundInquiry/types'

/**
 * ITEM 표 선택 행의 바코드 인쇄 — 실인쇄 부분만 남긴 순수 모듈.
 *
 * 규격·매수 입력 UI 는 컨덕터 뷰(`../handoff/page.generated` 바코드 출력 모달)가 그리고, 엔트리(`../index.tsx`)가
 * 모달 [확인] 슬롯에서 이 함수를 부른다. 옛 `BarcodePrintModal`(JsBarcode CODE128 + 인쇄 창, `productManagement` 이식)의
 * 인쇄 부분을 그대로 옮겼다 — `core.BarcodeSheetPreview` 가 게시본에 없어 미리보기는 없다(D12).
 *
 * 규격은 3종(A4(3X8)·A4(3X4)·A4(바코드))만 실제로 인쇄된다. 라벨(1X2)은 컬럼 수·라벨 높이 정의가 없어
 * 셸 옵션에서 뺐고(index.tsx `SPECSELECT_OPTIONS`), 그래도 들어오면 인쇄를 막고 안내한다.
 */
export const IMPLEMENTED_SPECS = ['A4(3X8)', 'A4(3X4)', 'A4(바코드)'] as const
export type ImplementedSpec = (typeof IMPLEMENTED_SPECS)[number]
/** 모달 규격 칸 기본값 — 생성본 셀렉트는 `defaultValue="A4(3X8)"` 로 그려지지만 폼 버스에는 onChange 때만 값이 실려 손대지 않으면 비어 있다. 배선이 화면에 보이는 이 값으로 채운다(index.tsx form 슬롯·_ok 폴백). */
export const DEFAULT_PRINT_SPEC: ImplementedSpec = IMPLEMENTED_SPECS[0]
/**
 * 모달 매수 칸 기본값 — 생성본 컨트롤의 `defaultValue`(1)와 같은 값. 규격과 마찬가지로 폼 버스에는 onChange 때만 실려 손대지 않으면 비어 있고,
 * 배선이 화면에 보이는 값으로 채운다(리뷰 지적 2026-09-29). 매수는 행마다 같은 장수로 찍는다.
 */
export const DEFAULT_PRINT_COPIES = 1

/** 인쇄 창 크기(px) — A4 세로 한 장이 스크롤 없이 보이는 폭·높이(옛 BarcodePrintModal·상품관리 2.0 과 같은 값). */
const PRINT_WINDOW_WIDTH = 900
const PRINT_WINDOW_HEIGHT = 1200

const SPEC_LAYOUT: Record<ImplementedSpec, { columns: number; labelHeightMm: number }> = {
  'A4(3X8)': { columns: 3, labelHeightMm: 34 },
  'A4(3X4)': { columns: 3, labelHeightMm: 69 },
  'A4(바코드)': { columns: 1, labelHeightMm: 60 },
}

// TODO(spec): 라벨(1X2) 컬럼 수·라벨 높이 정의 필요 — 정해지면 SPEC_LAYOUT 에 추가하고 IMPLEMENTED_SPECS 에 포함한다.
export const isImplementedSpec = (spec: string): spec is ImplementedSpec => (IMPLEMENTED_SPECS as readonly string[]).includes(spec)

// gdsNm/gdsCd/barcode 는 응답값을 그대로 쓰지만 문서에 넣기 전 반드시 이스케이프(XSS 방지)
const escapeHtml = (value: string): string =>
  value.replaceAll(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c)

const barcodeSvg = (value: string): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  try {
    JsBarcode(svg, value, { format: 'CODE128', displayValue: true, fontSize: 12, height: 40, margin: 4 })
  } catch {
    return `<div class="invalid">잘못된 바코드: ${escapeHtml(value)}</div>`
  }
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet')
  svg.style.maxWidth = '100%'
  svg.style.maxHeight = '70%'
  return svg.outerHTML
}

/** 인쇄 가능한 행 — 바코드가 있는 행만. */
export const printableRows = (rows: readonly GetInboundDetailsItem[]): GetInboundDetailsItem[] => rows.filter((r) => !!r.barcode)

/**
 * 인쇄 창을 열어 라벨을 찍는다. 행별 매수는 호출부가 정한다(엔트리는 모달 매수 칸 값을 전 행에 균일하게 준다).
 * @returns 팝업이 차단돼 열지 못했으면 false
 */
export const openBarcodePrintWindow = (
  rows: readonly GetInboundDetailsItem[],
  spec: ImplementedSpec,
  copiesForRow: (row: GetInboundDetailsItem) => number,
): boolean => {
  const layout = SPEC_LAYOUT[spec]
  const labels = printableRows(rows)
    .flatMap((r) =>
      Array.from(
        { length: copiesForRow(r) },
        () => `
      <div class="label">
        ${barcodeSvg(String(r.barcode))}
        <div class="name">${escapeHtml(r.gdsNm ?? '')}</div>
        <div class="code">${escapeHtml(r.gdsCd ?? '')}</div>
      </div>`,
      ),
    )
    .join('')
  const win = window.open('', '_blank', `width=${PRINT_WINDOW_WIDTH},height=${PRINT_WINDOW_HEIGHT}`)
  if (!win) {
    notice.failure('팝업이 차단되어 인쇄 창을 열 수 없습니다. 팝업을 허용한 뒤 다시 시도해 주세요.')
    return false
  }
  // document.write 는 deprecated API 라 DOM 조작으로 대체한다(window.open('') 이 이미 빈 document 를 준다).
  win.document.title = `바코드 프린트 (${spec})`
  const style = win.document.createElement('style')
  style.textContent = `
      @page { size: A4; margin: 8mm; }
      body { margin: 0; font-family: 'Pretendard', sans-serif; }
      .sheet { display: grid; grid-template-columns: repeat(${layout.columns}, 1fr); gap: 2mm; }
      .label { height: ${layout.labelHeightMm}mm; border: 0.2mm dashed #ccc; display: flex; flex-direction: column;
               align-items: center; justify-content: center; overflow: hidden; padding: 1mm; box-sizing: border-box;
               break-inside: avoid; text-align: center; }
      .name { font-size: 10px; width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .code { font-size: 9px; color: #555; }
      .invalid { color: red; font-size: 10px; }
    `
  win.document.head.appendChild(style)
  const sheet = win.document.createElement('div')
  sheet.className = 'sheet'
  sheet.innerHTML = labels
  win.document.body.appendChild(sheet)
  win.focus()
  win.print()
  return true
}
